/*
 * Knative Traffic Card Component
 * Glassmorphism + Monospace Style
 */

import React from 'react';
import { Box, Typography, Card, CardHeader, CardContent, Tooltip } from '@material-ui/core';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import { useKubernetesEnhancedStyles, colors, monoFont } from '../styles/theme';
import { KnativeTrafficTarget } from '../types';

interface KnativeTrafficCardProps {
  traffic: KnativeTrafficTarget[];
  loading?: boolean;
}

interface TrafficItemProps {
  target: KnativeTrafficTarget;
  index: number;
}

// 颜色数组，用于区分不同的流量目标
const trafficColors = [
  { bg: 'rgba(14, 165, 233, 0.15)', border: 'rgba(14, 165, 233, 0.3)', color: '#0ea5e9', gradient: colors.gradientCyan },
  { bg: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.3)', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)' },
  { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.3)', color: '#10b981', gradient: colors.gradientSuccess },
  { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)', color: '#f59e0b', gradient: colors.gradientWarning },
];

const TrafficItem: React.FC<TrafficItemProps> = ({ target, index }) => {
  const classes = useKubernetesEnhancedStyles();
  const colorSet = trafficColors[index % trafficColors.length];
  
  return (
    <Box 
      className={classes.deploymentItem}
      style={{
        borderLeft: `3px solid ${colorSet.color}`,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <Typography style={{
            fontFamily: monoFont,
            fontWeight: 600,
            fontSize: '0.9rem',
            color: colors.textPrimary,
          }}>
            {target.revisionName || 'latest'}
          </Typography>
          {target.latestRevision && (
            <Typography style={{
              fontFamily: monoFont,
              fontSize: '0.65rem',
              color: colors.textMuted,
              background: 'rgba(255,255,255,0.5)',
              padding: '2px 6px',
              borderRadius: 4,
            }}>
              @latest
            </Typography>
          )}
          {target.tag && (
            <Typography style={{
              fontFamily: monoFont,
              fontSize: '0.65rem',
              color: colorSet.color,
              background: colorSet.bg,
              border: `1px solid ${colorSet.border}`,
              padding: '2px 6px',
              borderRadius: 4,
            }}>
              {target.tag}
            </Typography>
          )}
        </Box>
        
        {/* Traffic Percentage */}
        <Typography style={{
          fontFamily: monoFont,
          fontWeight: 700,
          fontSize: '1.5rem',
          background: colorSet.gradient,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
        }}>
          {target.percent}%
        </Typography>
      </Box>
      
      {/* Traffic Bar */}
      <Box className={classes.progressBar} style={{ height: 8, marginBottom: 12 }}>
        <Box
          className={classes.progressBarFill}
          style={{
            width: `${target.percent}%`,
            background: colorSet.gradient,
            boxShadow: `0 0 12px ${colorSet.color}`,
          }}
        />
      </Box>
      
      {/* URL if available */}
      {target.url && (
        <Tooltip title="Open revision URL" arrow>
          <a
            href={target.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: colorSet.color,
              textDecoration: 'none',
              fontFamily: monoFont,
              fontSize: '0.7rem',
            }}
          >
            <OpenInNewIcon style={{ fontSize: '0.85rem' }} />
            {target.url.replace(/^https?:\/\//, '').slice(0, 40)}...
          </a>
        </Tooltip>
      )}
    </Box>
  );
};

export const KnativeTrafficCard: React.FC<KnativeTrafficCardProps> = ({
  traffic,
  loading,
}) => {
  const classes = useKubernetesEnhancedStyles();
  
  // 计算总流量（应该是 100%）
  const totalTraffic = traffic.reduce((sum, t) => sum + (t.percent || 0), 0);
  
  return (
    <Card className={classes.card}>
      <CardHeader
        title="// TRAFFIC_SPLIT"
        className={classes.cardHeader}
        subheader={
          <Typography style={{ 
            fontSize: '0.7rem', 
            color: colors.textMuted,
            fontFamily: monoFont,
            letterSpacing: '0.5px',
          }}>
            {traffic.length} target{traffic.length !== 1 ? 's' : ''} • {totalTraffic}% total
          </Typography>
        }
      />
      <CardContent className={classes.cardContent}>
        {loading ? (
          <Box className={classes.loading}>
            <Typography>loading...</Typography>
          </Box>
        ) : traffic.length === 0 ? (
          <Box className={classes.empty}>
            <Typography>no traffic configured</Typography>
          </Box>
        ) : (
          traffic.map((target, index) => (
            <TrafficItem 
              key={target.revisionName || `latest-${index}`} 
              target={target} 
              index={index}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
};

